/**
 * Admin Panel TypeScript Interfaces
 *
 * This file contains all interfaces used throughout the admin panel module.
 * Centralizing interfaces here keeps the service files clean and provides
 * a single source of truth for type definitions.
 */

/**
 * Configuration interface for admin routes
 * Used to structure route data for menu generation
 */
export interface AdminRouteConfig {
  path: string;        // Route path (e.g., 'users', 'dashboard')
  breadcrumb: string;  // Display name for menu item
  page: string;        // Page identifier for icon mapping
  icon?: string;       // Optional explicit icon override
}

/**
 * Interface for card design data
 */
export interface CardDesign {
  id: string;
  orderId: string;
  fileUrl: string;
  side: 'Front' | 'Back';
  isTemporary: boolean;
  createdDate: string;
  modifiedDate: string;
}

/**
 * Interface for QR Code details on an order (backend contract)
 */
export interface QrCode {
  id: string;
  orderId: string;
  fileUrl: string;
  side: string; // typically 'QR'
  isTemporary: boolean;
  createdDate: string;
  modifiedDate: string;
}

/**
 * Interface for DBC Order data
 */
export interface DBCOrder {
  id: string;
  userId: string;
  totalAmount: number;
  quantity: number;
  productId: string;
  status: string;
  projectId: string;
  createdDate: string;
  modifiedDate: string;
  frontDesign: CardDesign | null;
  backDesign: CardDesign | null;
  address: any | null;
  qrCode: QrCode | null;
  /** Convenience field for UI bindings */
  qrFileUrl?: string;
}

/** Lightweight DTO returned by paged endpoint (can extend later). */
export interface OrderResponse extends DBCOrder { }

/** Generic paged list wrapper (mirrors backend contract). */
export interface PagedList<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages?: number;
  hasNext: boolean;
  hasPrevious?: boolean;
}

/**
 * Interface for User data
 */
export interface User {
  id: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  email?: string;
  createdDate?: string;
  modifiedDate?: string;
  isActive?: boolean;
  subscriptionStatus?: string;
  billingId?: string;
  planType?: string;
  subscriptionEndDate?: string;
  subscriptionId?: string;
  // Add other user properties as needed
}

export interface UserDeletionResult {
  operationId: string;
  userId: string;
  billingId?: string | null;
  source: 'billing-and-identity' | 'identity' | 'identity-fallback';
  stripe: UserDeletionStep;
  identity: UserDeletionStep;
  reconciliationStatus: 'in-progress' | 'action-required' | 'retry-pending' | 'completed' | 'exhausted';
  nextAttemptAtUtc?: string | null;
  correlationId: string;
}

export interface UserDeletionStep {
  status: 'pending' | 'deleted' | 'already-absent' | 'not-requested' | 'failed';
  detail?: string;
  outcome?: 'hard-deleted' | 'soft-deleted-orders-retained' | string;
}

export type UserDeletionOperationStatus = 'in-progress' | 'action-required' | 'retry-pending' | 'completed' | 'exhausted';

export interface UserDeletionOperation {
  operationId: string;
  userId: string;
  billingId?: string | null;
  actorId: string;
  reason: string;
  correlationId: string;
  status: UserDeletionOperationStatus;
  stripe: UserDeletionStep;
  identity: UserDeletionStep;
  attemptCount: number;
  requestedAtUtc: string;
  updatedAtUtc: string;
  nextAttemptAtUtc?: string | null;
  completedAtUtc?: string | null;
  lastError?: string | null;
}

export interface UserDeletionOperationsPage {
  items: UserDeletionOperation[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
}

/**
 * Interface for organization data shown in the admin panel.
 */
export interface AdminOrganization {
  id: number;
  name?: string;
  billingKey?: string;
  slug?: string;
  email?: string;
  phoneNumber?: string;
  addressId?: number;
  website?: string;
  defaultTimezone?: string;
  defaultInviteRole?: string;
  logoUrl?: string;
  isPersonal?: boolean;
  stripeCustomerId?: string;
  createdByUserId?: string;
  billingOwnerUserId?: string;
  subscriptionId?: string;
  createdDate?: string;
  modifiedDate?: string;
  subscriptionStatus?: string;
  subscriptionInterval?: string;
  subscriptionEndDate?: string;
  freeTrial?: boolean;
  status?: string;
  totalMembers?: number;
  usedSeats?: number;
  totalSeats?: number;
  isDeleted?: boolean;
  deletedDate?: string | null;
  deletedByUserId?: string | null;
  address?: AdminOrganizationAddress | null;
}

/**
 * Interface for organization address data.
 */
export interface AdminOrganizationAddress {
  id?: number;
  street?: string;
  street1?: string;
  city?: string;
  state?: string;
  zip?: string;
  type?: string;
  orderId?: string | number | null;
  createdDate?: string;
  modifiedDate?: string;
}

/**
 * Interface for organization members shown in the admin panel.
 */
export interface AdminOrganizationMember {
  membershipId?: string;
  userId: string;
  name?: string;
  email?: string;
  role?: string;
  status?: string;
  lastActiveUtc?: string;
}

export interface BillingOwnerTransferResult {
  organizationId: number;
  previousBillingOwnerUserId: string;
  newBillingOwnerUserId: string;
  updatedAtUtc: string;
}

/**
 * Interface for organization audit log rows shown in the admin panel.
 */
export interface AdminOrganizationAuditLog {
  id: string;
  createdDate?: string;
  actorUserId?: string;
  actorName?: string;
  action?: string;
  summary?: string;
  targetType?: string | null;
  targetId?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
}

/**
 * Interface for detailed organization audit log record.
 */
export interface AdminOrganizationAuditDetail extends AdminOrganizationAuditLog {
  metadata?: Record<string, unknown> | null;
  metadataJson?: string | null;
}

export interface AdminUsageScope {
  organizationId: number;
  organizationName: string;
  billingId: string;
}

export interface AdminUsagePeriod {
  startUtc: string;
  endUtc: string;
}

export interface AdminUsageBreakdown {
  key: string;
  reservedCustomerUnits: number;
  settledCustomerUnits: number;
  deliveredCustomerUnits: number;
  vendorUnits: number;
  vendorCost: number;
  operationCount: number;
}

export interface AdminUsageSummary {
  scope: AdminUsageScope;
  period: AdminUsagePeriod;
  mode: 'Disabled' | 'Observe' | 'DualWrite' | 'Authoritative';
  reservedCustomerUnits: number;
  settledCustomerUnits: number;
  deliveredCustomerUnits: number;
  vendorUnits: number;
  vendorCost: number;
  operationCount: number;
  updatedAtUtc?: string | null;
  byOperation: AdminUsageBreakdown[];
}

export interface AdminUsageOperation {
  operationId: string;
  operationKey: string;
  allowanceKey: string;
  providerName: string;
  costCategory: string;
  state: string;
  reservedCustomerUnits: number;
  settledCustomerUnits: number;
  deliveredCustomerUnits: number;
  vendorUnits: number;
  vendorCost: number;
  cacheStatus?: string | null;
  parentOperationId?: string | null;
  providerTaskId?: string | null;
  providerEndpoint?: string | null;
  traceId?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface AdminUsageOperationsPage {
  scope: AdminUsageScope;
  period: AdminUsagePeriod;
  mode: AdminUsageSummary['mode'];
  items: AdminUsageOperation[];
  page: number;
  pageSize: number;
  totalCount: number;
}

/**
 * Interface for Address data (can be used in DBCOrder)
 */
export interface Address {
  id?: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

/**
 * Interface for API Response wrapper
 */
export interface ApiResponse<T> {
  data: T | null;
  error: any | null;
  success?: boolean;
  message?: string;
  totalCount?: number;
}

/**
 * Interface for Admin Dashboard Statistics
 */
export interface AdminStats {
  totalUsers: number;
  totalOrders: number;
  pendingOrders: number;
  completedOrders: number;
  totalRevenue: number;
}

/**
 * Enum for Order Status
 */
export enum OrderStatus {
  PENDING = 'Pending',
  PROCESSING = 'Processing',
  COMPLETED = 'Completed',
  CANCELLED = 'Cancelled',
  SHIPPED = 'Shipped'
}

/**
 * Enum for Card Design Side
 */
export enum CardSide {
  FRONT = 'Front',
  BACK = 'Back'
}
